// api.js
import axios from 'axios';
import { triggerSessionExpired } from '../utils/authEvents';
import { triggerErrorToast } from '../utils/toastEvents';
import { APIURL } from './urlUtils';

let lastNetworkToastAt = 0;
const NETWORK_TOAST_COOLDOWN_MS = 5000;
let sessionExpiredTriggered = false;

const maybeShowNetworkToast = (message) => {
    const now = Date.now();
    if (now - lastNetworkToastAt < NETWORK_TOAST_COOLDOWN_MS) return;
    lastNetworkToastAt = now;
    triggerErrorToast(message);
};

const api = axios.create({
    baseURL: APIURL,
    timeout: 15000,
    headers: 
        {
            'Content-Type': 'application/json',
        },
    withCredentials: true, // This ensures cookies are sent with requests
});

// Use global interceptor so that:
// 1. If session expires, log user out by trigger
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            // Avoid loops: Don't trigger if we are already on the login page
            // (a 401 there just means "wrong credentials", not "session expired")
            if (window.location.pathname !== '/login') {
                // Trigger the event so the React UI logs the user out and redirects
                // immediately, only once even if several requests 401 at once.
                if (!sessionExpiredTriggered) {
                    sessionExpiredTriggered = true;
                    triggerSessionExpired();
                }
                // The app is about to navigate to /login, so swallow this
                // rejection instead of letting it reach the calling code's own
                // catch block, which would otherwise surface a generic
                // "Unknown error" toast/alert right as the redirect happens.
                return new Promise(() => {});
            }
        } else if (error.code === 'ECONNABORTED') {
            maybeShowNetworkToast('Request timed out. The server may be offline or overloaded.');
        } else if (!error.response) {
            maybeShowNetworkToast('Cannot connect to server. Please check network or server status.');
        } else if (error.response.status >= 500) {
            maybeShowNetworkToast('Server error occurred. Please try again shortly.');
        }
        return Promise.reject(error);
    }
);

export default api;
